import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import axios from '@axios';
import jwtServiceConfig from '../auth/services/jwtService/jwtServiceConfig';
import { jwtDecode } from 'jwt-decode';
import history from '@/@history';
import routeConfigs from '@/app/configs/routesConfig';
import { matchPath } from 'react-router-dom';
import {
  getUser,
  getUserLogo as getUserLogoJs,
  resetUserState as resetUserStateJs,
} from './userSlice';
import { setInitSettings } from 'app/store/fuse/settingsSlice';
import { RootState } from './rootReducer';

const guestRoutes = routeConfigs.filter((route) => route.auth?.length === 0);
const generallyAvailableRoutesRoutes = routeConfigs.filter((route) =>
  route.auth?.includes('Guest'),
);

interface Payload {
  email: string;
  password: string;
}

interface NextHomeParams {
  source: string;
  user: string;
  time: string;
  hash: string;
}

/**
 * return custom error message from backend if present
 */
const extractError = (error: any) => {
  return error?.response?.data?.error?.message || 'Something went wrong';
};

// workaround for incorrect type deferrence; remove once we fully migrate to TS
const getUserLogo = getUserLogoJs as (id: string) => any;
const resetUserState = resetUserStateJs as () => any;

export const signInUser = createAsyncThunk(
  'auth/signIn',
  async ({ email, password }: Payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        jwtServiceConfig.signIn,
        {
          email,
          password,
        },
        { withCredentials: true },
      );
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.delete(jwtServiceConfig.signOut, { withCredentials: true });
      await dispatch(setInitSettings());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post(jwtServiceConfig.refreshToken, null, {
        withCredentials: true,
      });
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;

      return response.data;
    } catch (error) {
      console.error(error);
      const isOnlyGuestRoute = guestRoutes.some((route) =>
        matchPath(route.path, location.pathname),
      );
      const generallyAvailableRoutes = generallyAvailableRoutesRoutes.some((route) =>
        matchPath(route.path, location.pathname),
      );

      if (isOnlyGuestRoute || generallyAvailableRoutes) {
        return;
      }
      history.push('/sign-in');
      return rejectWithValue('Something went , please login again');
    }
  },
);

export const nextHomeSignIn = createAsyncThunk(
  'auth/nextHomeSignIn',
  async (params: NextHomeParams, { rejectWithValue }) => {
    try {
      const response = await axios.get(jwtServiceConfig.nextHomeSignIn, {
        params,
        withCredentials: true,
      });
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const nextHomeSignInAndLoad = createAsyncThunk(
  'auth/nextHomeSignInAndLoad',
  async (params: NextHomeParams, { dispatch }) => {
    await dispatch(nextHomeSignIn(params)).unwrap();
    const user = await dispatch(getUser()).unwrap();
    await dispatch(getUserLogo(user.id)).unwrap();
    history.push('/orders');
  },
);

export const impersonateUser = createAsyncThunk(
  'auth/impersonateUser',
  async (impersonateUserId: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(
        jwtServiceConfig.impersonateUser,
        { impersonateUserId },
        { withCredentials: true },
      );
      dispatch(resetUserState());
      // Lazy import to avoid circular dependency
      const { apiSlice } = await import('../api/apiSlice');
      dispatch(apiSlice.util.resetApiState());
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractError(error));
    }
  },
);

export interface AuthState {
  loading: boolean;
  userToken: string | undefined | null;
  refreshToken: string | null;
  tokenExpiry: number | null;
  error: unknown;
  success: boolean;
  nextHomeStatus: 'in progress' | 'succeeded' | 'failed' | null;
}

const initialState: AuthState = {
  loading: false,
  userToken: undefined,
  refreshToken: null,
  tokenExpiry: null,
  error: null,
  success: false,
  nextHomeStatus: null,
};

export const isTokenExpired = (state: AuthState) => {
  if (state.tokenExpiry) {
    return Date.now() > state.tokenExpiry;
  }
  return false;
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(refreshToken.fulfilled, (state, { payload }) => {
      if (!payload) {
        return;
      }
      const decoded: any = jwtDecode(payload?.accessToken);

      state.userToken = payload.accessToken;
      state.refreshToken = decoded.refreshToken;
      state.tokenExpiry = decoded.exp * 1000;
    });
    builder.addCase(refreshToken.pending, (state) => {
      state.loading = false;
      state.error = true;
      state.userToken = null;
    });
    builder.addCase(refreshToken.rejected, (state) => {
      state.loading = false;
      state.error = true;
      state.userToken = null;
    });
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.loading = false;
      state.userToken = null;
      state.refreshToken = null;
      state.tokenExpiry = null;
      state.error = null;
      state.success = false;
    });
    builder.addCase(nextHomeSignInAndLoad.pending, (state) => {
      state.nextHomeStatus = 'in progress';
    });
    builder.addCase(nextHomeSignInAndLoad.rejected, (state) => {
      state.nextHomeStatus = 'failed';
    });
    builder.addCase(nextHomeSignInAndLoad.fulfilled, (state) => {
      state.nextHomeStatus = 'succeeded';
    });
    builder.addMatcher(
      isAnyOf(signInUser.fulfilled, nextHomeSignIn.fulfilled, impersonateUser.fulfilled),
      (state, { payload }) => {
        if (!payload) {
          return;
        }
        state.loading = false;
        state.success = true;

        const decoded: any = jwtDecode(payload.accessToken);

        state.userToken = payload.accessToken;
        state.refreshToken = decoded.refreshToken;
        state.tokenExpiry = decoded.exp * 1000;
      },
    );
    builder.addMatcher(
      isAnyOf(signInUser.pending, nextHomeSignIn.pending, impersonateUser.pending),
      (state) => {
        state.loading = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      isAnyOf(signInUser.rejected, nextHomeSignIn.rejected, impersonateUser.rejected),
      (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.userToken = null;
      },
    );
  },
});

export const selectAuth = ({ auth }: RootState): AuthState => auth;

export default authSlice.reducer;
